/**
 * dimensionScoreInput.ts — Adapter Store-Felder → Core-DimensionData.
 *
 * Die 5 Freitext-Dimensionen speichern im Wizard-Store als
 * `fields: Record<string,string>` (siehe lib/store/wizard.ts). Typisierte
 * Werte (bool/number/enum/array) werden dabei als String serialisiert. Diese
 * Datei kapselt (a) die Feld-Schlüssel als einzige Wahrheit, (b) das
 * De-/Serialisieren, (c) den Aufbau der Core-`XData`-Objekte für die
 * Score-Engine.
 *
 * Bewusst KEIN neuer Store-Slice: Strings passen zum bestehenden Zwei-Ebenen-
 * Sync (dimension_data.structured_data.fields) und zur Berater-Review-UI.
 */
import type {
  IdentityData,
  ActivityData,
  StyleData,
  HealthData,
  PrivateData,
  Qualification,
  PersonalTrait,
  ChildcareStatus,
  Hours,
} from '@tragfaehigkeit/core';

/** number → Hours-Brand (nonneg-Semantik, wie im Resources-Adapter). */
function asHours(n: number | undefined): Hours | undefined {
  return n === undefined ? undefined : (n as Hours);
}

export type Fields = Readonly<Record<string, string>>;

// ---------------------------------------------------------------------------
// De-/Serialisierung
// ---------------------------------------------------------------------------
export function serializeBool(b: boolean): string {
  return b ? 'true' : 'false';
}
export function parseBool(v: string | undefined): boolean {
  return v === 'true';
}
/** Für tri-state/optional-boolean-Felder: unterscheidet unbeantwortet. */
export function parseOptionalBool(v: string | undefined): boolean | undefined {
  if (v === 'true') return true;
  if (v === 'false') return false;
  return undefined;
}
export function serializeNum(n: number): string {
  return String(n);
}
export function parseNum(v: string | undefined): number | undefined {
  if (v === undefined || v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}
export function serializeList<T>(items: readonly T[]): string {
  return JSON.stringify(items);
}
export function parseList<T>(v: string | undefined): T[] {
  if (!v || v.trim() === '') return [];
  try {
    const parsed: unknown = JSON.parse(v);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}
function trimmedOrUndefined(v: string | undefined): string | undefined {
  const t = (v ?? '').trim();
  return t.length > 0 ? t : undefined;
}

// ---------------------------------------------------------------------------
// Feld-Schlüssel je Dimension (einzige Wahrheit; Steps + Adapter teilen sie)
// ---------------------------------------------------------------------------
export const IDENTITY_KEYS = {
  qualifications: 'qualifications',
  industryExperienceYears: 'industryExperienceYears',
  hasEntrepreneurialExperience: 'hasEntrepreneurialExperience',
  yearsSelfEmployed: 'yearsSelfEmployed',
  usp: 'usp',
  personalTraits: 'personalTraits',
  notes: 'notes',
} as const;

export const ACTIVITY_KEYS = {
  valueProposition: 'valueProposition',
  targetCustomers: 'targetCustomers',
  topClientsByRevenue: 'topClientsByRevenue',
  hasRecurringRevenue: 'hasRecurringRevenue',
  recurringRevenueSharePercent: 'recurringRevenueSharePercent',
  scalability: 'scalability',
  hasDocumentedSalesProcess: 'hasDocumentedSalesProcess',
  notes: 'notes',
} as const;

export const STYLE_KEYS = {
  targetGroupDefined: 'targetGroupDefined',
  targetGroupDescription: 'targetGroupDescription',
  competitors: 'competitors',
  differentiators: 'differentiators',
  selfReportedHourlyRateEuro: 'selfReportedHourlyRateEuro',
  industryFloorHourlyRateEuro: 'industryFloorHourlyRateEuro',
  pricingStrategyDocumented: 'pricingStrategyDocumented',
  notes: 'notes',
} as const;

export const HEALTH_KEYS = {
  selfReportedResilienceScore: 'selfReportedResilienceScore',
  hoursWorkedPerWeek: 'hoursWorkedPerWeek',
  hasMedicalCertification: 'hasMedicalCertification',
  chronicConditionsExist: 'chronicConditionsExist',
  conditionsAffectWork: 'conditionsAffectWork',
  copingStrategiesDescribed: 'copingStrategiesDescribed',
  pastCrisisHandlingNotes: 'pastCrisisHandlingNotes',
  notes: 'notes',
} as const;

export const PRIVATE_KEYS = {
  childcareStatus: 'childcareStatus',
  hasCareResponsibility: 'hasCareResponsibility',
  careResponsibilityDescription: 'careResponsibilityDescription',
  zumutbarkeitsgruende: 'zumutbarkeitsgruende',
  regionalLaborMarketContext: 'regionalLaborMarketContext',
  notes: 'notes',
} as const;

// ---------------------------------------------------------------------------
// Adapter: Fields → Core-DimensionData
// ---------------------------------------------------------------------------
export function buildIdentityData(f: Fields): IdentityData {
  return {
    dimension: 'identity',
    qualifications: parseList<Qualification>(f[IDENTITY_KEYS.qualifications]),
    industryExperienceYears: asHours(parseNum(f[IDENTITY_KEYS.industryExperienceYears])),
    hasEntrepreneurialExperience: parseBool(f[IDENTITY_KEYS.hasEntrepreneurialExperience]),
    yearsSelfEmployed: parseNum(f[IDENTITY_KEYS.yearsSelfEmployed]),
    usp: trimmedOrUndefined(f[IDENTITY_KEYS.usp]),
    personalTraits: parseList<PersonalTrait>(f[IDENTITY_KEYS.personalTraits]),
    notes: trimmedOrUndefined(f[IDENTITY_KEYS.notes]),
  };
}

const SCALABILITY = new Set(['linear', 'partly_scalable', 'highly_scalable', 'unknown']);
export function buildActivityData(f: Fields): ActivityData {
  const scal = f[ACTIVITY_KEYS.scalability];
  return {
    dimension: 'activity',
    valueProposition: trimmedOrUndefined(f[ACTIVITY_KEYS.valueProposition]),
    targetCustomers: trimmedOrUndefined(f[ACTIVITY_KEYS.targetCustomers]),
    topClientsByRevenue: parseList<{ id: string; revenueSharePercent: number }>(
      f[ACTIVITY_KEYS.topClientsByRevenue],
    ),
    hasRecurringRevenue: parseBool(f[ACTIVITY_KEYS.hasRecurringRevenue]),
    recurringRevenueSharePercent: parseNum(f[ACTIVITY_KEYS.recurringRevenueSharePercent]),
    scalability: SCALABILITY.has(scal ?? '') ? (scal as ActivityData['scalability']) : 'unknown',
    hasDocumentedSalesProcess: parseBool(f[ACTIVITY_KEYS.hasDocumentedSalesProcess]),
    notes: trimmedOrUndefined(f[ACTIVITY_KEYS.notes]),
  };
}

export function buildStyleData(f: Fields): StyleData {
  return {
    dimension: 'style',
    targetGroupDefined: parseBool(f[STYLE_KEYS.targetGroupDefined]),
    targetGroupDescription: trimmedOrUndefined(f[STYLE_KEYS.targetGroupDescription]),
    competitors: parseList<string>(f[STYLE_KEYS.competitors])
      .map((s) => s.trim())
      .filter((s) => s.length > 0),
    differentiators: trimmedOrUndefined(f[STYLE_KEYS.differentiators]),
    selfReportedHourlyRateEuro: parseNum(f[STYLE_KEYS.selfReportedHourlyRateEuro]),
    industryFloorHourlyRateEuro: parseNum(f[STYLE_KEYS.industryFloorHourlyRateEuro]),
    pricingStrategyDocumented: parseBool(f[STYLE_KEYS.pricingStrategyDocumented]),
    notes: trimmedOrUndefined(f[STYLE_KEYS.notes]),
  };
}

export function buildHealthData(f: Fields): HealthData {
  return {
    dimension: 'health',
    selfReportedResilienceScore: parseNum(f[HEALTH_KEYS.selfReportedResilienceScore]),
    hoursWorkedPerWeek: asHours(parseNum(f[HEALTH_KEYS.hoursWorkedPerWeek])),
    hasMedicalCertification: parseBool(f[HEALTH_KEYS.hasMedicalCertification]),
    chronicConditionsExist: parseBool(f[HEALTH_KEYS.chronicConditionsExist]),
    conditionsAffectWork: parseBool(f[HEALTH_KEYS.conditionsAffectWork]),
    copingStrategiesDescribed: trimmedOrUndefined(f[HEALTH_KEYS.copingStrategiesDescribed]),
    pastCrisisHandlingNotes: trimmedOrUndefined(f[HEALTH_KEYS.pastCrisisHandlingNotes]),
    notes: trimmedOrUndefined(f[HEALTH_KEYS.notes]),
  };
}

const CHILDCARE = new Set(['not_applicable', 'secured', 'not_secured']);
export function buildPrivateData(f: Fields): PrivateData {
  const cc = f[PRIVATE_KEYS.childcareStatus];
  return {
    dimension: 'private',
    childcareStatus: (CHILDCARE.has(cc ?? '') ? cc : 'not_applicable') as ChildcareStatus,
    hasCareResponsibility: parseBool(f[PRIVATE_KEYS.hasCareResponsibility]),
    careResponsibilityDescription: trimmedOrUndefined(
      f[PRIVATE_KEYS.careResponsibilityDescription],
    ),
    zumutbarkeitsgruende: trimmedOrUndefined(f[PRIVATE_KEYS.zumutbarkeitsgruende]),
    regionalLaborMarketContext: trimmedOrUndefined(f[PRIVATE_KEYS.regionalLaborMarketContext]),
    notes: trimmedOrUndefined(f[PRIVATE_KEYS.notes]),
  };
}
